import { Command } from "cmdk";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";
import { Popover } from "radix-ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { useIsMobile } from "../hooks/use-media-query";
import { cn } from "../lib/cn";
import { useUiText } from "../lib/locale";
import type { Option } from "../lib/option";
import { controlSize, fieldShell, type ControlSize } from "../lib/variants";
import { useField } from "./field";
import { Modal } from "./modal";
import { Spinner } from "./spinner";

type Common<V extends string> = {
  /** Статический список (фильтруется на клиенте). */
  options?: Option<V>[];
  /** Удалённый поиск (debounce 300 мс). Вызывается и с пустой строкой при открытии. */
  loadOptions?: (query: string, signal: AbortSignal) => Promise<Option<V>[]>;
  /** Подписи уже выбранных значений, которых нет в текущем списке (для loadOptions). */
  selectedOptions?: Option<V>[];
  /** «Создать „…“» внизу списка (например, нового клиента). */
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  label?: string;
  clearable?: boolean;
  size?: ControlSize;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
};

export type SearchSelectProps<V extends string = string> = Common<V> &
  (
    | { multiple?: false; value: V | null; onValueChange: (value: V | null, option: Option<V> | null) => void }
    | { multiple: true; value: V[]; onValueChange: (value: V[], options: Option<V>[]) => void }
  );

/** Выбор с поиском: справочники, клиенты, товары. Desktop — popover, mobile — bottom sheet. */
export function SearchSelect<V extends string = string>(props: SearchSelectProps<V>) {
  const {
    options, loadOptions, selectedOptions, onCreate, label, clearable, size = "md", invalid, disabled, className, id,
  } = props;
  const field = useField();
  const text = useUiText();
  const { createLabel = text.create, placeholder = text.select, searchPlaceholder = text.search, emptyText = text.nothingFound } = props;
  const isMobile = useIsMobile();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 300);
  const [remote, setRemote] = useState<Option<V>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const known = useRef(new Map<V, Option<V>>());
  const inputRef = useRef<HTMLInputElement>(null);

  // Фокус в поиск сразу при открытии (и в popover, и в bottom sheet): после кадра, чтобы
  // FocusScope Radix не перехватил его (у Modal он ставит фокус на первый элемент — крестик).
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open || !loadOptions) return;
    const ctrl = new AbortController();
    setLoading(true);
    setError(false);
    loadOptions(debounced, ctrl.signal)
      .then((r) => { setRemote(r); setLoading(false); })
      .catch(() => { if (!ctrl.signal.aborted) { setError(true); setLoading(false); } });
    return () => ctrl.abort();
  }, [open, debounced, loadOptions]);

  const list = loadOptions ? remote : (options ?? []);
  for (const o of [...(options ?? []), ...remote, ...(selectedOptions ?? [])]) known.current.set(o.value, o);

  const selectedValues = useMemo<V[]>(() => (props.multiple ? props.value : props.value === null ? [] : [props.value]), [props.multiple, props.value]);
  const selected = selectedValues.map((v) => known.current.get(v) ?? { value: v, label: String(v) });

  const toggle = (o: Option<V>) => {
    if (props.multiple) {
      const next = selectedValues.includes(o.value) ? selectedValues.filter((v) => v !== o.value) : [...selectedValues, o.value];
      props.onValueChange(next, next.map((v) => known.current.get(v)!).filter(Boolean));
    } else {
      props.onValueChange(o.value, o);
      setOpen(false);
    }
  };
  const clear = () => (props.multiple ? props.onValueChange([], []) : props.onValueChange(null, null));
  const onOpenChange = (o: boolean) => { setOpen(o); if (!o) setQuery(""); };

  const trigger = (
    <button
      type="button"
      id={id ?? field?.id}
      disabled={isDisabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field?.describedBy}
      data-invalid={isInvalid}
      data-disabled={!!isDisabled}
      onClick={() => isMobile && onOpenChange(true)}
      className={cn(fieldShell, controlSize({ size }), "justify-between text-start outline-none focus-visible:border-primary", className)}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {selected.length === 0 && <span className="truncate text-placeholder">{placeholder}</span>}
        {!props.multiple && selected[0] && <span className="truncate">{selected[0].label}</span>}
        {props.multiple &&
          selected.slice(0, 2).map((o) => (
            <span key={o.value} className="max-w-[45%] truncate rounded-md bg-surface-2 px-2 py-0.5 text-xs">{o.label}</span>
          ))}
        {props.multiple && selected.length > 2 && <span className="shrink-0 text-xs text-muted">+{selected.length - 2}</span>}
      </span>
      {clearable && selected.length > 0 && !isDisabled ? (
        <span
          role="button"
          tabIndex={-1}
          aria-label={text.clear}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); clear(); }}
          className="grid size-6 shrink-0 place-items-center rounded text-muted hover:bg-surface-2"
        >
          <X className="size-4" />
        </span>
      ) : (
        <ChevronDown className="size-4 shrink-0 text-muted" />
      )}
    </button>
  );

  const itemClass =
    "flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none select-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-surface-2  [&_svg]:size-4 [&_svg]:shrink-0";

  const panel: ReactNode = (
    <Command shouldFilter={!loadOptions} loop className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3">
        <Search className="size-4 shrink-0 text-muted" />
        <Command.Input
          ref={inputRef}
          autoFocus
          value={query}
          onValueChange={setQuery}
          placeholder={searchPlaceholder}
          className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-placeholder"
        />
        {loading && <Spinner className="text-muted" />}
      </div>
      <Command.List className="max-h-[min(50dvh,320px)] overflow-y-auto overscroll-contain p-1 max-sm:max-h-none max-sm:flex-1">
        {!loading && (
          <Command.Empty className="px-3 py-6 text-center text-sm text-muted">{error ? text.loadError : emptyText}</Command.Empty>
        )}
        {list.map((o) => {
          const isSel = selectedValues.includes(o.value);
          return (
            <Command.Item
              key={o.value}
              value={`${o.label} ${o.description ?? ""} ${(o.keywords ?? []).join(" ")} ${o.value}`}
              disabled={o.disabled}
              onSelect={() => toggle(o)}
              className={itemClass}
            >
              {props.multiple && (
                <span className={cn("grid size-4 place-items-center rounded border", isSel ? "border-primary bg-primary text-primary-fg" : "border-border")}>
                  {isSel && <Check className="size-3!" />}
                </span>
              )}
              {o.icon}
              <div className="min-w-0 flex-1">
                <div className="truncate">{o.label}</div>
                {o.description && <div className="truncate text-xs text-muted">{o.description}</div>}
              </div>
              {!props.multiple && isSel && <Check className="text-primary" />}
            </Command.Item>
          );
        })}
        {onCreate && query.trim() && !loading && (
          <Command.Item value={`__create__ ${query}`} onSelect={() => { onCreate(query.trim()); onOpenChange(false); }} className={cn(itemClass, "text-primary")}>
            <Plus /> {createLabel(query.trim())}
          </Command.Item>
        )}
      </Command.List>
    </Command>
  );

  if (isMobile)
    return (
      <>
        {trigger}
        <Modal open={open} onOpenChange={onOpenChange} title={label ?? placeholder} size="md" flush className="h-[85dvh]" bodyClassName="flex flex-col">
          {panel}
        </Modal>
      </>
    );

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-[60] flex w-[max(var(--radix-popover-trigger-width),280px)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-tile border border-border bg-surface text-fg shadow-xl data-[state=open]:animate-in"
        >
          {panel}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
